/* eslint-disable react/no-unknown-property -- Firebolt consumes the custom css prop at build time. */
// Рынок города: склад и продажа ресурсов, каталог построек, хозяйство.
// Первый экран, реализованный по макету дизайнера (Marketplace.dc.html,
// «городской рынок») поверх живой экономики цепочек: данные — screenData
// 'economy' + 'player', действия — грид-команды /prod (сервер оплачивает,
// проверяет и отвечает; экран ничего не считает сам, кроме подсказок).
// Вся сборка данных — в buildEconomyMarket (screen-data.js), под тестами.
import { css } from '@firebolt-dev/css'
import { useEffect, useMemo, useRef, useState } from 'react'
import { CoinsIcon, FactoryIcon, PauseIcon, PlayIcon, RefreshCwIcon, UsersIcon, WarehouseIcon } from 'lucide-react'

import { COLORS, ICON_SIZES, RADII, TYPE, space } from '../theme'
import { PRODUCTION, buildEconomyMarket, economyView, playerWallet, refreshScreens, screenData, subscribeScreenData } from '../screen-data'
import { Btn, MetaStat, Panel, ScreenShell } from './VoiceKit'
import { EconIcon } from './EconIcon'

export function EconomyScreen({ world, onClose }) {
  const [economy, setEconomy] = useState(() => economyView(world))
  const [wallet, setWallet] = useState(() => playerWallet(world))
  const [loaded, setLoaded] = useState(() => screenData(world, 'economy') !== null)
  const [amounts, setAmounts] = useState({})

  const seenResult = useRef(null)
  useEffect(() => {
    const offEconomy = subscribeScreenData(world, 'economy', () => {
      const next = economyView(world)
      setEconomy(next); setLoaded(true)
      // Отказ сервера обязан быть виден на экране, а не только в чате.
      const result = next.result
      if (result?.requestId && result.requestId !== seenResult.current) {
        seenResult.current = result.requestId
        world.ui.notify({
          kind: result.ok ? 'success' : 'error',
          title: result.ok ? 'РЫНОК ОБНОВЛЁН' : 'ОПЕРАЦИЯ ОТКЛОНЕНА',
          message: result.message || (result.ok ? 'Сервер подтвердил операцию.' : 'Сервер отклонил операцию.'),
          dedupeKey: `economy:${result.requestId}`,
        })
      }
    })
    const offPlayer = subscribeScreenData(world, 'player', () => setWallet(playerWallet(world)))
    // Единственный источник свежести — сервер: просим полный пуш экранов.
    refreshScreens(world)
    return () => { offEconomy(); offPlayer() }
  }, [world])

  useEffect(() => {
    const onKey = event => { if (event.key === 'Escape') onClose?.() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const view = useMemo(() => buildEconomyMarket(economy, wallet.balance), [economy, wallet])

  const sell = async item => {
    const amount = Math.min(item.have, Math.max(1, Number.parseInt(amounts[item.id], 10) || 1))
    const confirmed = await world.ui.confirm({
      kind: 'confirm', tone: 'warning', safeEnter: false,
      title: 'ПРОДАТЬ РЫНКУ?',
      message: `${item.name} ×${amount} по ${item.price} МК за штуку. Сервер зачислит ${item.price * amount} МК после проверки склада.`,
      dedupeKey: `economy:sell:${item.id}`,
    })
    if (confirmed) PRODUCTION.sell(world, item.id, amount)
  }

  const build = async item => {
    const confirmed = await world.ui.confirm({
      kind: 'purchase', tone: 'warning', safeEnter: false,
      title: 'ПОСТРОИТЬ?',
      message: `${item.name} за ${item.cost} МК. Сервер проверит баланс и поставит постройку в хозяйство участка.`,
      dedupeKey: `economy:build:${item.id}`,
    })
    if (confirmed) PRODUCTION.build(world, item.id)
  }

  return <ScreenShell label='WORLD' title='РЫНОК ГОРОДА' subtitle='Экономика цепочек: склад, скупка по базовым ценам, стройка производств и жилья. Все операции подтверждает сервер мира.' ariaLabel='Рынок города TESTCS World' onClose={onClose}
    meta={<>
      <MetaStat icon={<CoinsIcon size={ICON_SIZES.sm} color={COLORS.warning} />} label='БАЛАНС' value={`${view.balance.toLocaleString('ru-RU')} МК`} />
      <MetaStat icon={<WarehouseIcon size={ICON_SIZES.sm} color={COLORS.cyan} />} label='СКЛАД' value={String(view.stockTotal)} />
      <MetaStat icon={<FactoryIcon size={ICON_SIZES.sm} color={COLORS.success} />} label='ПОСТРОЙКИ' value={String(view.buildings.length)} />
    </>}>
    <div css={bodyCss}>
      {!loaded ? <Empty title='ЗАГРУЖАЕМ РЫНОК' text='Запрашиваем каталог, цены и склад у сервера мира.' />
        : !view.available ? <Empty title='РЫНОК НЕДОСТУПЕН' text='Сервер не прислал каталог экономики — витрина не выдумывается локально.' />
        : <>
        <Panel title='РЫНОК СКУПАЕТ' info={`Базовые цены каталога. Покупка у рынка — наценка ×${view.buyMarkup}. Продажа списывает со склада и зачисляет МК сервером.`}>
          <div css={goodsCss}>
            {view.goods.map(item => <article key={item.id} css={goodCss} data-empty={item.have === 0 || undefined}>
              <span css={goodIconCss}><EconIcon id={item.id} size={28} /></span>
              <div css={goodInfoCss}>
                <strong>{item.name}</strong>
                <span css={mutedCss}>{item.price} МК/шт · на складе {item.have}</span>
              </div>
              {item.have > 0 && <div css={sellCss}>
                <input aria-label={`Сколько продать: ${item.name}`} inputMode='numeric' placeholder='1'
                  value={amounts[item.id] || ''}
                  onChange={event => setAmounts(current => ({ ...current, [item.id]: event.target.value.replace(/\D/g, '').slice(0, 4) }))} />
                <Btn size='small' onClick={() => sell(item)}>ПРОДАТЬ</Btn>
              </div>}
            </article>)}
          </div>
        </Panel>

        <Panel title='ПОСТРОИТЬ' info='Цена списывается сервером. Производства требуют рабочих и сырьё, жильё даёт места жителям и налог.'>
          <div css={catalogCss}>
            {view.catalog.map(item => <article key={item.id} css={cardCss}>
              <header css={cardHeadCss}>
                <span css={goodIconCss}><EconIcon id={item.id} size={30} /></span>
                <div><strong>{item.name}</strong><div css={mutedCss}>{item.house ? 'жильё' : `${item.workers} раб.`}</div></div>
              </header>
              {!item.house && <div css={recipeCss}>
                {item.recipe.input.map(part => <span key={part.id} css={recipePartCss}><EconIcon id={part.id} size={14} /> {part.name} ×{part.amount}</span>)}
                {item.recipe.input.length > 0 && <span css={mutedCss}>→</span>}
                {item.recipe.output.map(part => <span key={part.id} css={recipePartCss}><EconIcon id={part.id} size={14} /> {part.name} ×{part.amount}</span>)}
              </div>}
              <footer css={cardFootCss}>
                <strong css={priceCss}>{item.cost.toLocaleString('ru-RU')} МК</strong>
                {item.affordable
                  ? <Btn size='small' onClick={() => build(item)}>ПОСТРОИТЬ</Btn>
                  : <span css={missingCss}>не хватает {item.missing.toLocaleString('ru-RU')} МК</span>}
              </footer>
            </article>)}
          </div>
        </Panel>

        <div css={twoColCss}>
          <Panel title={`МОИ ПРОИЗВОДСТВА · ${view.buildings.length}`} info='Пауза останавливает цикл и освобождает рабочих. Причины простоя присылает сервер.'>
            {!view.buildings.length ? <Empty title='ПОКА ПУСТО' text='Построй первое производство — цепочка начнётся со склада подъёмных.' /> :
              <div css={listCss}>{view.buildings.map(item => <div key={item.index} css={rowCss}>
                <strong>{item.name}</strong>
                <span css={item.idleReason || item.paused ? warnCss : okCss}>{item.paused ? 'пауза' : item.idleReason || 'работает'}</span>
                <Btn size='small' variant='ghost' aria-label={item.paused ? `Запустить: ${item.name}` : `Остановить: ${item.name}`} onClick={() => PRODUCTION.pause(world, item.index)}>
                  {item.paused ? <PlayIcon size={ICON_SIZES.xs} /> : <PauseIcon size={ICON_SIZES.xs} />}
                </Btn>
              </div>)}</div>}
          </Panel>
          <Panel title='НАСЕЛЕНИЕ И РАБОТА' info='Жители селятся, пока хватает еды и товаров их уровня; налог капает за каждого.'>
            <div css={listCss}>
              {view.population.houses.map(item => <div key={`h-${item.name}`} css={rowCss}><UsersIcon size={ICON_SIZES.xs} /><span>{item.name}</span><strong>{item.count} дом.</strong></div>)}
              {view.population.residents.map(item => <div key={`r-${item.name}`} css={rowCss}><span css={mutedCss}>жители</span><span>{item.name}</span><strong>{item.count}</strong></div>)}
              {view.population.workers && <div css={rowCss}><span css={mutedCss}>рабочие</span><span>занято {view.population.workers.required}</span><strong css={view.population.workers.shortage ? warnCss : okCss}>{view.population.workers.shortage ? `не хватает ${view.population.workers.shortage}` : `свободно ${view.population.workers.available - view.population.workers.required}`}</strong></div>}
              {!view.population.houses.length && !view.population.residents.length && <Empty title='НИКТО НЕ ЗАСЕЛИЛСЯ' text='Поставь дом поселенцев — жители придут, если на складе есть еда.' />}
            </div>
          </Panel>
        </div>

        <div css={refreshCss}><Btn variant='ghost' onClick={() => refreshScreens(world)}><RefreshCwIcon size={ICON_SIZES.xs} /> ОБНОВИТЬ</Btn></div>
      </>}
    </div>
  </ScreenShell>
}

function Empty({ title, text }) { return <div role='status' css={emptyCss}><strong>{title}</strong><span>{text}</span></div> }

const bodyCss = css`flex:1;min-height:0;padding:${space(2)};overflow-y:auto;display:grid;align-content:start;gap:${space(2)};`
const goodsCss = css`display:grid;grid-template-columns:repeat(auto-fill,minmax(230px,1fr));gap:${space(1)};`
const goodCss = css`display:flex;align-items:center;flex-wrap:wrap;gap:${space(1)};padding:${space(1)};border:1px solid ${COLORS.border};border-radius:${RADII.lg};background:${COLORS.bgPanelLight};&[data-empty]{opacity:.55;}`
const goodIconCss = css`display:grid;place-items:center;width:44px;height:44px;flex:none;border-radius:${RADII.md};border:1px solid ${COLORS.border};color:${COLORS.cyan};background:${COLORS.bgElevated};`
const goodInfoCss = css`flex:1 1 120px;min-width:0;display:grid;gap:2px;`
const sellCss = css`display:flex;gap:6px;align-items:center;input{width:52px;min-height:36px;text-align:center;border:1px solid ${COLORS.borderStrong};border-radius:${RADII.md};background:${COLORS.bgElevated};color:${COLORS.text};}`
const catalogCss = css`display:grid;grid-template-columns:repeat(auto-fill,minmax(250px,1fr));gap:${space(1)};`
const cardCss = css`display:grid;align-content:start;gap:${space(1)};padding:${space(1)};border:1px solid ${COLORS.border};border-radius:${RADII.xl};background:${COLORS.bgPanelLight};`
const cardHeadCss = css`display:flex;align-items:center;gap:${space(1)};`
const recipeCss = css`display:flex;flex-wrap:wrap;align-items:center;gap:6px;`
const recipePartCss = css`display:inline-flex;align-items:center;gap:4px;padding:2px 8px;border-radius:${RADII.pill};border:1px solid ${COLORS.border};font-size:${TYPE.caption.size};color:${COLORS.textSecondary};`
const cardFootCss = css`display:flex;align-items:center;justify-content:space-between;gap:${space(1)};`
const priceCss = css`color:${COLORS.warning};`
const missingCss = css`font-size:${TYPE.caption.size};color:${COLORS.textSecondary};`
const twoColCss = css`display:grid;grid-template-columns:repeat(auto-fit,minmax(280px,1fr));gap:${space(2)};`
const listCss = css`display:grid;gap:6px;`
const rowCss = css`display:flex;align-items:center;gap:${space(1)};padding:6px ${space(1)};border:1px solid ${COLORS.border};border-radius:${RADII.md};background:${COLORS.bgPanelLight};span:first-of-type{flex:none;}strong:last-child,span:last-child{margin-left:auto;}`
const okCss = css`color:${COLORS.success};font-size:${TYPE.caption.size};`
const warnCss = css`color:${COLORS.warning};font-size:${TYPE.caption.size};`
const mutedCss = css`font-size:${TYPE.caption.size};color:${COLORS.textSecondary};`
const refreshCss = css`display:flex;justify-content:end;`
const emptyCss = css`min-height:120px;display:grid;place-items:center;align-content:center;gap:${space(1)};text-align:center;color:${COLORS.textSecondary};strong{color:${COLORS.text};letter-spacing:.06em;}`
